import React, { useState, useEffect, useLayoutEffect, useContext } from 'react';
import { ScrollView, View, StyleSheet, Text } from 'react-native';
import BallotForm from './BallotForm';
import CustomSnackbar from '../../../../components/CustomSnackbar';
import LinkStyleButton from '../../../../components/Buttons/LinkStyleButton';
import { MainColors, Status } from '../../../../constants/Colors';
import FontSizes from '../../../../constants/FontSizes';
import DeleteModal from '../../../../components/Modals/DeleteModal';
import { HeaderBackButton } from '@react-navigation/stack';
import HeaderButton from '../../../../components/Buttons/HeaderButton';
import InfoMessageBox from '../../../../components/InfoMessageBox';
import { OrganizationContext } from '../../../../context/OrganizationProvider';
import useFetch from '../../../../utils/useFetch'
import environment from '../../../../utils/environment'
const CreateOrEditBallotScreen = ({ ballot, setBallot, isCreateBallotScreen, navigation, setShowBallotItemScreen, setBallotItem }) => {

    const { organizationId } = useContext(OrganizationContext);
    const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
    const [deleteButtonDisabled, setDeleteButtonDisabled] = useState(true);
    const [snackbarVisible, setSnackbarVisible] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState();
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [isModalDeleteButtonDisabled, setIsModalDeleteButtonDisabled] = useState(true);
    const [deleteInputValue, setDeleteInputValue] = useState();
    const [areTextInputsEditable, setAreTextInputsEditable] = useState(false);
    const [areDatePickersDisabled, setAreDatePickersDisabled] = useState(true);

    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: isCreateBallotScreen ? 'Create Ballot' : 'Edit Ballot',
            headerRight: () => (
                <HeaderButton
                    onPress={onSaveBallotChangesPressedHandler}
                    disabled={saveButtonDisabled}
                    accessibilityLabel="Save"
                    accessibilityHint="Press this button to save changes to this ballot"
                    buttonText="Save"
                />
            ),
            headerLeft: () => (
                <HeaderBackButton
                    onPress={() => {
                        navigation.goBack()
                    }}
                    label="Back"
                    tintColor="#fff"
                />
            )
        });
    }, [navigation, saveButtonDisabled, ballot]);


    useEffect(() => {
        if (ballot.title && ballot.startDate && ballot.endDate && ballot.ballotItems.length > 0) {
            if (ballot.canBallotBeModified) {
                setSaveButtonDisabled(false);
                setDeleteButtonDisabled(false);
                setAreTextInputsEditable(true);
                setAreDatePickersDisabled(false);
            }
        } else {
            setSaveButtonDisabled(true);
            setDeleteButtonDisabled(true);
            setAreTextInputsEditable(false);
            setAreDatePickersDisabled(true);
        }

        if (isCreateBallotScreen) {
            setSaveButtonDisabled(false);
            setDeleteButtonDisabled(false);
            setAreTextInputsEditable(true);
            setAreDatePickersDisabled(false);
        }
    }, [ballot, isCreateBallotScreen]);

    const onSnackbarDismissedHandler = () => {
        setSnackbarVisible(false);
        navigation.navigate('AdminBallotsDashboard');
    }

    const { post, put, doDelete } = useFetch()
    const onSaveBallotChangesPressedHandler = async () => {
        const body = {
            ...ballot,
            organizationId
        }
        if (isCreateBallotScreen) {
            await post(`/api/ballots`, body)
        } else {
            await put(`/api/ballots/${ballot.ballotId}`, body)
        }

        setSaveButtonDisabled(true);
        setSnackbarMessage('Ballot changes were saved.');
        setSnackbarVisible(true);
    }

    const onDeleteInputTextChangedHandler = (deleteInputValue) => {
        setDeleteInputValue(deleteInputValue);
        if (deleteInputValue === 'DELETE') {
            setIsModalDeleteButtonDisabled(false);
        } else {
            setIsModalDeleteButtonDisabled(true);
        }
    }

    const onDeleteBallotPressedHandler = async () => {
        console.log('deleting')
        const url = `/api/ballots/${ballot.ballotId}`;

        const serverBallot = await doDelete(url, { ballotId: ballot.ballotId })

        setIsDeleteModalVisible(false);
        setSaveButtonDisabled(true);
        setSnackbarMessage('Ballot was successfully deleted.');
        setSnackbarVisible(true);
    }
    return (
        <>
            <ScrollView
                style={styles.scrollViewContainer}
                keyboardDismissMode="on-drag"
                keyboardShouldPersistTaps="handled"
                contentInsetAdjustmentBehavior="always"
            >
                <View style={styles.contentContainer}>
                    {ballot.status && !ballot.canBallotBeModified ? (
                        <InfoMessageBox style={{ marginBottom: 30 }} messageText={`This ballot is ${ballot.status.toLowerCase()} and cannot be modified.`} />
                    ) : (null)}

                    <BallotForm
                        // Title
                        titleValue={ballot.title}
                        onChangeTitleText={title => setBallot(b => ({ ...b, title }))}
                        titleEditable={areTextInputsEditable}

                        // Description
                        descriptionValue={ballot.description}
                        onChangeDescriptionText={description => setBallot(b => ({ ...b, description }))}
                        descriptionEditable={areTextInputsEditable}

                        // Start Date
                        startDateValue={ballot.startDate}
                        onChangeStartDateText={startDate => setBallot(b => ({ ...b, startDate }))}
                        startDateDisabled={areDatePickersDisabled}

                        // End Date
                        endDateValue={ballot.endDate}
                        onChangeEndDateText={endDate => setBallot(b => ({ ...b, endDate }))}
                        endDateDisabled={areDatePickersDisabled}

                        // Ballot Items
                        noBallotItemsMessage={isCreateBallotScreen ? `There's nothing to vote on yet. Let's add a ballot item!` : `No ballot items to show.`}

                        onBallotItemNextButtonPress={() => {
                            setBallotItem({ choices: [] })
                            setShowBallotItemScreen(true)
                        }}

                        ballotItemsListData={ballot.ballotItems}

                        onEditBallotItemPressed={(ballotItem) => {
                            setBallotItem(ballotItem)
                            setShowBallotItemScreen(true)
                        }}

                        addBallotItemButtonDisabled={saveButtonDisabled}

                        // Minimum Votes to be Valid
                        turnoutValueLabel={ballot.turnoutPercentToBeValid}
                        turnoutValue={ballot.turnoutPercentToBeValid}
                        onChangeTurnoutValue={turnoutPercentToBeValid => setBallot(b => ({ ...b, turnoutPercentToBeValid }))}
                        turnoutSliderDisabled={saveButtonDisabled}

                        // Majority Required to Win
                        majorityValueLabel={ballot.majorityPercentToWin}
                        majorityValue={ballot.majorityPercentToWin}
                        onChangeMajorityValue={majorityPercentToWin => setBallot(b => ({ ...b, majorityPercentToWin }))}
                        majoritySliderDisabled={saveButtonDisabled}

                        // Save Button
                        formButtonText='Save Changes'
                        formButtonAccessibilityLabel='Save changes'
                        formButtonAccessibilityHint='Press this button to save changes for this ballot'
                        formButtonDisabled={saveButtonDisabled}
                        formButtonPressed={onSaveBallotChangesPressedHandler}
                    />

                    {/* Delete Ballot Button */}
                    {!isCreateBallotScreen ? (
                        <LinkStyleButton
                            disabled={deleteButtonDisabled}
                            style={deleteButtonDisabled ? styles.disabledDeleteButton : styles.deleteButton}
                            buttonText='Delete Ballot'
                            accessibilityLabel='Delete Ballot'
                            accessibilityHint='Press this button to delete this ballot'
                            onPress={() => setIsDeleteModalVisible(true)}
                        />
                    ) : (null)}

                    {ballot.status && !ballot.canBallotBeModified ? (
                        <InfoMessageBox style={{ marginTop: 30 }} messageText={`This ballot is ${ballot.status.toLowerCase()} and cannot be modified.`} />
                    ) : (null)}
                </View>
            </ScrollView>
            <CustomSnackbar
                message={snackbarMessage}
                visible={snackbarVisible}
                onDismiss={onSnackbarDismissedHandler}
                actionLabel='GO BACK'
                actionOnPress={() => navigation.goBack()}
            />
            <DeleteModal
                visible={isDeleteModalVisible}
                deleteButtonDisabled={isModalDeleteButtonDisabled}
                onModalBackgroundPressed={() => setIsDeleteModalVisible(false)}
                onCancelPressed={() => setIsDeleteModalVisible(false)}
                itemToDelete={ballot.title}
                deleteInputValue={deleteInputValue}
                onChangeDeleteInputText={onDeleteInputTextChangedHandler}
                onModalDeletePress={onDeleteBallotPressedHandler}
            />
        </>
    )
}

const styles = StyleSheet.create({
    scrollViewContainer: {
        flexGrow: 1,
    },
    contentContainer: {
        paddingVertical: 30,
        paddingHorizontal: 20
    },
    disabledDeleteButton: {
        marginTop: 20,
        color: MainColors.lightGray,
        fontWeight: '500',
        fontSize: FontSizes.medium
    },
    deleteButton: {
        marginTop: 20,
        color: MainColors.danger,
        fontWeight: '500',
        fontSize: FontSizes.medium
    },
});

export default CreateOrEditBallotScreen;